import {
  Button,
  Container,
  IconButton,
  Modal,
  Paper,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { Level, Playlist, PlaylistCollection } from "./Playlists";
import { useState } from "react";
import { Info, PlayArrow, SelectAll, Timer } from "@mui/icons-material";
import DifficultyRating from "./Difficulty";
import { glassColor } from "./Colors";

enum Stage {
  ChoosePlaylist = "ChoosePlaylist",
  ChooseLevel = "ChooseLevel",
}

export default function PlaylistWindow({
  allPlaylists,
  open,
  setOpen,
  loadLevel,
  anchorEl,
}: {
  allPlaylists: PlaylistCollection | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  loadLevel: (playlist: Playlist, level: Level) => void;
  anchorEl: HTMLElement | null;
}) {
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist>();
  const [stage, setStage] = useState(Stage.ChoosePlaylist);

  const [selectedLevel, setSelectedLevel] = useState<Level>();

  let stageDescription = () => {
    if (stage === Stage.ChoosePlaylist) {
      return "Select Playlist";
    } else if (stage === Stage.ChooseLevel) {
      return "Select Level";
    }
  };

  let stageOverview = () => {
    if (stage === Stage.ChoosePlaylist) {
      return "Select a playlist to load a level";
    } else if (stage === Stage.ChooseLevel) {
      return `Select a level from "${selectedPlaylist?.NameMenu}"`;
    }
  };

  let stageAction = () => {
    if (stage === Stage.ChoosePlaylist) {
      setStage(Stage.ChooseLevel);
      setSelectedLevel(selectedPlaylist?.levels[0]);
    } else if (stage === Stage.ChooseLevel) {
      if (selectedLevel) {
        if (selectedPlaylist) {
          loadLevel(selectedPlaylist, selectedLevel);
          cleanup();
        } else {
          alert("Please select a playlist");
        }
        setOpen(false);
      } else {
        alert("Please select a level");
      }
    }
  };

  let cleanup = () => {
    setStage(Stage.ChoosePlaylist);
  };

  let stageContent = () => {
    if (stage === Stage.ChoosePlaylist) {
      return allPlaylists?.playlists.map((playlist) => {
        if (playlist.levels.length === 0) {
          return null;
        }
        return (
          <PlayListItem
            key={playlist.UniqueID}
            playlist={playlist}
            selected={selectedPlaylist?.UniqueID === playlist.UniqueID}
            setSelected={(selected) => {
              if (selected) {
                setSelectedPlaylist(playlist);
              }
            }}
            stageAction={stageAction}
          />
        );
      });
    } else if (stage === Stage.ChooseLevel) {
      return (
        <>
          <div style={{ display: "flex", flexDirection: "row" }}></div>
          {selectedPlaylist?.levels.map((level) => {
            return (
              <LevelItem
                key={level.UniqueID}
                level={level}
                selected={selectedLevel?.UniqueID === level.UniqueID}
                setSelected={(selected) => {
                  if (selected) {
                    setSelectedLevel(level);
                  }
                }}
                stageAction={stageAction}
              />
            );
          })}
        </>
      );
    }
  };

  return (
    <Popover
      open={open}
      onClose={() => {
        cleanup();
        setOpen(false);
      }}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 2,
            backgroundColor: glassColor(),
            color: "secondary.main",
            backdropFilter: "blur(10px)",
          },
        },
      }}
    >
      <div>
        <Button
          variant="contained"
          onClick={() => {
            stageAction();
          }}
        >
          {stageDescription()}
        </Button>
        <Typography variant="h6" color="secondary" fontWeight={"bold"}>
          {stageOverview()}
        </Typography>
        {stage === Stage.ChooseLevel ? (
          <Button
            variant="contained"
            sx={{ marginBottom: 1 }}
            onClick={() => {
              setStage(Stage.ChoosePlaylist);
            }}
          >
            Back
          </Button>
        ) : null}
        {stageContent()}

        {allPlaylists?.playlists.length === 0 ? (
          <Typography color="secondary" fontSize={12}>
            No playlists available. Create a playlist in Groove Catcher and
            reload.
          </Typography>
        ) : (
          <>
            <Button
              variant="contained"
              sx={{ marginRight: 1 }}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                stageAction();
              }}
            >
              {stageDescription()}
            </Button>
          </>
        )}
      </div>
    </Popover>
  );
}

function PlayListItem({
  playlist,
  selected,
  setSelected,
  stageAction,
}: {
  playlist: Playlist;
  selected: boolean;
  setSelected: (selected: boolean) => void;
  stageAction: () => void;
}) {
  return (
    <Paper
      sx={{
        width: "100%",
        height: "10%",
        marginBottom: 1,
        backgroundColor: selected ? "#5B5A4BFF" : "#434648FF",
        display: "flex",
        padding: 1,
        overflow: "auto",
        cursor: "pointer",
        justifyContent: "space-between",
      }}
      onClick={() => {
        setSelected(!selected);
      }}
      onDoubleClick={() => {
        setSelected(!selected);
        stageAction();
      }}
    >
      <div
        style={{ display: "flex", flexDirection: "column", overflow: "auto" }}
      >
        <Typography color="secondary" fontWeight={"bold"}>
          {playlist.NameMenu}
        </Typography>
        <Typography color="secondary" fontSize={12}>
          {playlist.Notes}
        </Typography>
      </div>
    </Paper>
  );
}

function LevelItem({
  level,
  selected,
  setSelected,
  stageAction,
}: {
  level: Level;
  selected: boolean;
  setSelected: (selected: boolean) => void;
  stageAction: () => void;
}) {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  function getDifficultyColor() {
    var rating = DifficultyRating.getDifficultyRating(level.DifficultyRating);
    return rating.color;
  }

  return (
    <Paper
      sx={{
        width: "100%",
        height: "10%",
        marginBottom: 1,
        backgroundColor: selected ? "#5B5A4BFF" : "#434648FF",
        display: "flex",
        padding: 1,
        overflow: "auto",
        cursor: "pointer",
      }}
      onClick={() => {
        setSelected(!selected);
      }}
      onDoubleClick={() => {
        setSelected(!selected);
        stageAction();
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            overflow: "auto",
            alignItems: "center",
            alignContent: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              overflow: "auto",
              alignItems: "center",
              alignContent: "center",
            }}
          >
            <Typography color="secondary" fontWeight={"bold"}>
              {level.NameMenu}
            </Typography>
            <IconButton color="secondary" size="small" onClick={handleClick}>
              <Info fontSize="small" />
            </IconButton>
            <Paper
              sx={{
                display: "flex",
                flexDirection: "row",
                padding: 0.5,
                backgroundColor: getDifficultyColor(),
                alignContent: "center",
                alignItems: "center",
                marginLeft: 1,
              }}
            >
              <Typography
                sx={{ color: "black", fontWeight: "bold" }}
                fontSize={12}
              >
                {level.DifficultyRating}
              </Typography>
            </Paper>

            <Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
              <Paper sx={{ padding: 1, backgroundColor: "#434648" }}>
                <Typography color="secondary" fontWeight={"bold"} fontSize={12}>
                  Created: {level.DateCreated}
                </Typography>
                <Typography color="secondary" fontWeight={"bold"} fontSize={12}>
                  Last Modified: {level.DateModified}
                </Typography>
              </Paper>
            </Popover>
          </div>
          <Paper
            sx={{
              display: "flex",
              flexDirection: "row",
              padding: 0.5,
              backgroundColor: "#e6bf30",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Timer fontSize="small" />
            <Typography sx={{ color: "black" }} fontWeight="bold" fontSize={12}>
              {level.DurationString}
            </Typography>
          </Paper>
        </div>
        <Typography
          sx={{
            color: "gray",
          }}
          fontSize={12}
        >
          By {level.Creator}
        </Typography>
        <Typography color="secondary" fontSize={12}>
          {level.Notes}
        </Typography>
      </div>
    </Paper>
  );
}
